// Line-by-line paper review.
//
// The model returns JSON of the form
//   { verdict, changes: [{ id, original, corrected, reason }] }
// `original` is kept only when it occurs in the answer key. Applying a
// decision splices `corrected` in for "accept" and leaves the source span
// for "reject".

function stripThinking(raw) {
  let s = String(raw || '');
  s = s.replace(/<think>[\s\S]*?<\/think>/gi, ' ');
  s = s.replace(/<\|think\|>[\s\S]*?<\|\/think\|>/gi, ' ');
  const closed = s.search(/<\/think>/i);
  if (closed !== -1) s = s.slice(closed + '</think>'.length);
  return s;
}

function sliceBalanced(s, openAt) {
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = openAt; i < s.length; i += 1) {
    const ch = s[i];
    if (inStr) {
      if (esc) esc = false;
      else if (ch === '\\') esc = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') inStr = true;
    else if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) return s.slice(openAt, i + 1);
    }
  }
  return '';
}

function parseLoose(text) {
  const attempts = [text];
  const repaired = String(text).replace(/,\s*([}\]])/g, '$1');
  if (repaired !== text) attempts.push(repaired);
  for (const attempt of attempts) {
    try { return JSON.parse(attempt); } catch (_) { /* try the next shape */ }
  }
  return null;
}

function asReview(data) {
  if (Array.isArray(data)) return { verdict: data.length ? 'ISSUES_FOUND' : 'NO_ISSUES', changes: data };
  if (!data || typeof data !== 'object') return null;
  if (Array.isArray(data.changes) || data.verdict) return data;
  const original = data.original || data.old || data.from || data.quote || data.text;
  const corrected = data.corrected || data.new || data.to || data.fix || data.replacement;
  if (original || corrected) return { verdict: 'ISSUES_FOUND', changes: [data] };
  return null;
}

function stripToJson(raw) {
  let s = stripThinking(raw).trim();
  const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) s = fence[1].trim();
  const whole = parseLoose(s);
  if (asReview(whole)) return s;
  let best = '';
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] !== '{') continue;
    const slice = sliceBalanced(s, i);
    if (!slice) continue;
    if (asReview(parseLoose(slice))) best = slice;
    i += Math.max(0, slice.length - 1);
  }
  if (best) return best;
  const start = s.indexOf('{');
  const end = s.lastIndexOf('}');
  if (start !== -1 && end > start) return s.slice(start, end + 1);
  return s;
}

function salvageChanges(raw) {
  const s = stripThinking(raw);
  const items = [];
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] !== '{') continue;
    const slice = sliceBalanced(s, i);
    if (!slice) continue;
    const parsed = parseLoose(slice);
    const original = parsed && (parsed.original || parsed.old || parsed.from || parsed.quote || parsed.text);
    const corrected = parsed && (parsed.corrected || parsed.new || parsed.to || parsed.fix || parsed.replacement);
    if (original || corrected) items.push(parsed);
    i += Math.max(0, slice.length - 1);
  }
  return items;
}

function quotedText(value) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

function skipRecord(original, corrected, reason) {
  const quote = quotedText(original).replace(/\s+/g, ' ').trim();
  const fix = quotedText(corrected).replace(/\s+/g, ' ').trim();
  const why = quotedText(reason).replace(/\s+/g, ' ').trim();
  if (!quote && !fix && !why) return null;
  return {
    original: quote.slice(0, 400),
    corrected: fix.slice(0, 400),
    reason: why.slice(0, 240)
  };
}

function findUnused(source, needle, used) {
  if (!needle) return -1;
  let from = 0;
  while (from <= source.length) {
    const idx = source.indexOf(needle, from);
    if (idx === -1) return -1;
    const end = idx + needle.length;
    const overlaps = used.some((r) => idx < r.end && end > r.start);
    if (!overlaps) return idx;
    from = idx + 1;
  }
  return -1;
}

/**
 * Parse a model reply into anchored changes.
 * @returns {{ verdict: string, changes: Array, dropped: number, error: string }}
 */
export function parsePaperDiff(raw, sourceText) {
  const source = String(sourceText || '');
  let data = asReview(parseLoose(stripToJson(raw)));
  if (!data) {
    const salvaged = salvageChanges(raw);
    if (salvaged.length) data = { verdict: 'ISSUES_FOUND', changes: salvaged };
  }
  if (!data) {
    return { verdict: '', changes: [], dropped: 0, unmatched: [], error: 'invalid JSON' };
  }

  const verdict = String(data.verdict || '').trim();
  const list = Array.isArray(data.changes) ? data.changes : [];
  const used = [];
  const changes = [];
  const unmatched = [];
  let dropped = 0;

  list.forEach((item, i) => {
    if (!item || typeof item !== 'object') {
      dropped += 1;
      return;
    }
    const original = quotedText(item.original || item.old || item.from || item.quote || item.text);
    const corrected = quotedText(item.corrected || item.new || item.to || item.fix || item.replacement);
    const reason = quotedText(item.reason || item.why || item.note).trim();
    if (!original || original === corrected) {
      const record = skipRecord(original, corrected, reason);
      if (record) unmatched.push(record);
      dropped += 1;
      return;
    }
    const start = findUnused(source, original, used);
    if (start < 0) {
      dropped += 1;
      const record = skipRecord(original, corrected, reason);
      if (record) unmatched.push(record);
      return;
    }
    const end = start + original.length;
    used.push({ start, end });
    let id = String(item.id == null ? '' : item.id).trim() || `c${i + 1}`;
    while (changes.some((c) => c.id === id)) id = `${id}-${i + 1}`;
    changes.push({ id, original, corrected, reason, start, end });
  });

  changes.sort((a, b) => a.start - b.start);
  return { verdict, changes, dropped, unmatched, error: '' };
}

/**
 * Build the paper after the teacher's tick/cross choices.
 * A missing decision keeps the original span.
 */
export function applyPaperChanges(sourceText, changes, decisions) {
  const source = String(sourceText || '');
  const sorted = [...(changes || [])]
    .filter((c) => c && typeof c.start === 'number' && typeof c.end === 'number')
    .sort((a, b) => a.start - b.start);
  let out = '';
  let cursor = 0;
  for (const c of sorted) {
    if (c.start < cursor || c.end > source.length || c.start > c.end) continue;
    out += source.slice(cursor, c.start);
    if (decisions && decisions[c.id] === 'accept') out += String(c.corrected ?? '');
    else out += source.slice(c.start, c.end);
    cursor = c.end;
  }
  out += source.slice(cursor);
  return out;
}

export function allPaperDecisionsMade(changes, decisions) {
  return (changes || []).every(
    (c) => decisions && (decisions[c.id] === 'accept' || decisions[c.id] === 'reject')
  );
}

function charKind(ch) {
  if (!ch) return '';
  if (/[ivx]/i.test(ch)) return 'roman';
  if (/[0-9۰-۹]/.test(ch)) return 'digit';
  return '';
}

function spanParts(text, from, to) {
  const out = [];
  if (from > 0) out.push({ text: text.slice(0, from), mark: false });
  if (to > from) out.push({ text: text.slice(from, to), mark: true });
  if (to < text.length) out.push({ text: text.slice(to), mark: false });
  return out;
}

/**
 * The shared words stay plain. Only the changed characters are marked,
 * and a changed number is marked as a whole (ii vs iii, not just the extra i).
 */
export function inlineChangeSpans(before, after) {
  const a = String(before ?? '');
  const b = String(after ?? '');
  let start = 0;
  const limit = Math.min(a.length, b.length);
  while (start < limit && a[start] === b[start]) start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }
  const touch = charKind(a[start - 1]) || charKind(b[start - 1]) || charKind(a[endA]) || charKind(b[endB]);
  if (touch) {
    while (start > 0 && charKind(a[start - 1]) === touch && charKind(b[start - 1]) === touch) start -= 1;
    while (endA < a.length && endB < b.length && charKind(a[endA]) === touch && charKind(b[endB]) === touch) {
      endA += 1;
      endB += 1;
    }
  }
  if (endA === start && endB > start && start < a.length) {
    let wordEnd = start;
    while (wordEnd < a.length && /\s/.test(a[wordEnd])) wordEnd += 1;
    const wordStart = wordEnd;
    while (wordEnd < a.length && !/\s/.test(a[wordEnd])) wordEnd += 1;
    if (wordEnd > wordStart) endA = wordEnd;
  }
  return { old: spanParts(a, start, endA), neu: spanParts(b, start, endB) };
}

const URDU_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const MARK_RE = /([0-9۰-۹]+(?:[.][0-9۰-۹]+)?)\s*(نمبر(?:ز)?|marks?)/gi;
const IMPERATIVE_Q_RE = /(دیں|لکھیں|لکھیے|کیجیے|کیجئے)\s*؟/g;

function parseLocalizedNumber(digits) {
  const ascii = String(digits).replace(/[۰-۹]/g, (d) => String(URDU_DIGITS.indexOf(d)));
  const n = Number(ascii);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

function sumMarks(values) {
  const cents = values.reduce((total, n) => total + Math.round(Number(n) * 100), 0);
  return cents / 100;
}

function formatMarkNumber(sample, n) {
  const rounded = Math.round(Number(n) * 100) / 100;
  const text = Number.isInteger(rounded) ? String(rounded) : String(rounded);
  if (/[۰-۹]/.test(sample)) return text.replace(/\d/g, (d) => URDU_DIGITS[Number(d)]);
  return text;
}

function toSameDigits(sample, n) {
  const text = String(n);
  if (/[۰-۹]/.test(sample)) return text.replace(/\d/g, (d) => URDU_DIGITS[Number(d)]);
  return text;
}

function lineSpans(source) {
  const spans = [];
  let start = 0;
  for (let i = 0; i <= source.length; i += 1) {
    if (i === source.length || source[i] === '\n') {
      let textEnd = i;
      if (textEnd > start && source[textEnd - 1] === '\r') textEnd -= 1;
      spans.push({ start, textEnd, text: source.slice(start, textEnd) });
      start = i + 1;
    }
  }
  return spans;
}

function markMatches(text) {
  const out = [];
  const re = new RegExp(MARK_RE.source, 'gi');
  let m;
  while ((m = re.exec(text))) {
    const value = parseLocalizedNumber(m[1]);
    if (value == null) continue;
    out.push({ value, text: m[0], index: m.index, digits: m[1] });
  }
  return out;
}

function findInRange(source, needle, from, to, used) {
  if (!needle) return -1;
  let pos = Math.max(0, from);
  const limit = Math.min(source.length, to);
  while (pos <= limit) {
    const idx = source.indexOf(needle, pos);
    if (idx === -1 || idx + needle.length > limit) return -1;
    const end = idx + needle.length;
    const overlaps = used.some((r) => idx < r.end && end > r.start);
    if (!overlaps) return idx;
    pos = idx + 1;
  }
  return -1;
}

function isSectionHeader(line) {
  const stripped = String(line || '').replace(/^[\s*#>/_'"“”«»-]+/, '');
  return /سوال/.test(line) || /^Q(?:uestion)?\s*\d+/i.test(stripped);
}

function isSectionBanner(line) {
  const stripped = String(line || '').replace(/^[\s*#>/_'"“”«»-]+/, '');
  return /^section\b/i.test(stripped);
}

function questionLabel(text) {
  const trimmed = String(text || '').trim();
  const wrapped = trimmed.match(/^\(([^)]+)\)/);
  if (wrapped) return `(${wrapped[1].trim()})`;
  const label = readLabel(trimmed);
  if (label) return label.token;
  const named = trimmed.match(/^(Q(?:uestion)?\s*\d+[.)]?)/i);
  if (named) return named[1];
  return trimmed.slice(0, 24);
}

function lineForLabel(line) {
  return String(line || '').replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g, '');
}

const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii'];
const URDU_PARTS = ['الف', 'ب', 'ج', 'د', 'ہ', 'و'];
const LABEL_RE = /^\s*(?:\(\s*)?(xii|xi|ix|viii|vii|vi|iv|iii|ii|x|v|i|الف|ب|ج|د|[a-d])(\s*[.)۔])/i;

function readLabel(line) {
  const cleaned = lineForLabel(line);
  const match = cleaned.match(LABEL_RE);
  if (!match) return null;
  const punct = (match[2].match(/[.)۔]/) || ['.'])[0];
  const raw = match[1];
  const key = /^[ivx]+$/i.test(raw) ? raw.toLowerCase() : (/^[a-d]$/i.test(raw) ? raw.toLowerCase() : raw);
  const token = raw + punct;
  if (!String(line).includes(token) && !cleaned.includes(token)) return null;
  return { key, token, punct };
}

function numberingStyle(items) {
  const keys = items.map((item) => item.label && item.label.key).filter(Boolean);
  if (keys.some((key) => ROMAN.includes(key))) return 'roman';
  if (keys.some((key) => URDU_PARTS.includes(key))) return 'urdu';
  if (keys.some((key) => /^[a-d]$/.test(key))) return 'letter';
  return null;
}

function labelKind(key) {
  if (ROMAN.includes(key)) return 'roman';
  if (URDU_PARTS.includes(key)) return 'urdu';
  if (/^[a-d]$/.test(key)) return 'letter';
  return null;
}

function expectedKey(style, index) {
  if (style === 'roman') return ROMAN[index] || null;
  if (style === 'urdu') return URDU_PARTS[index] || null;
  if (style === 'letter') return String.fromCharCode(97 + index);
  return null;
}

function isAnswerLine(line) {
  const stripped = lineForLabel(line).replace(/^[\s*#>/_'"“”«»-]+/, '');
  return /^(جواب|answer\b|model answer\b)/i.test(stripped);
}

function isQuestionEcho(line) {
  const stripped = lineForLabel(line).replace(/^[\s*#>/_'"“”«»-]+/, '');
  return /^(question|سوال)\s*[:：]/i.test(stripped);
}

function headingFormula(text) {
  const match = String(text || '').match(/([0-9۰-۹]+(?:[.][0-9۰-۹]+)?)\s*[x×*]\s*([0-9۰-۹]+(?:[.][0-9۰-۹]+)?)\s*=\s*([0-9۰-۹]+(?:[.][0-9۰-۹]+)?)/);
  if (!match) return null;
  const count = parseLocalizedNumber(match[1]);
  const each = parseLocalizedNumber(match[2]);
  const stated = parseLocalizedNumber(match[3]);
  if (count == null || each == null || stated == null || count <= 0) return null;
  const times = Math.round(count);
  return { count: times, each, stated, product: sumMarks(Array.from({ length: times }, () => each)) };
}

function questionMarkValues(text) {
  if (!text.trim() || isAnswerLine(text) || isQuestionEcho(text)) return [];
  const marks = markMatches(text);
  return marks.length ? [marks[marks.length - 1].value] : [];
}

function auditLabelSequence(items, style, suggestions) {
  if (!style || items.length < 2) return;
  const written = items.map((item) => (item.label ? item.label.key : '—')).join(', ');
  const numbered = items.filter((item) => item.label).length;
  const sequenceBroken = numbered !== items.length || items.some((item, index) => {
    const want = expectedKey(style, index);
    return !item.label || item.label.key !== want;
  });
  if (!sequenceBroken) return;
  items.forEach((item, index) => {
    const want = expectedKey(style, index);
    if (!want || (item.label && item.label.key === want)) return;
    const reason = `This section has ${items.length} questions but only ${numbered} numbers (${written}). This question should be ${want}.`;
    if (!item.label) {
      const sep = style === 'urdu' ? '۔ ' : '. ';
      suggestions.push({
        original: item.line.text,
        corrected: `${want}${sep}${item.line.text}`,
        reason,
        searchFrom: item.line.start,
        searchTo: item.line.textEnd
      });
    } else {
      suggestions.push({
        original: item.label.token,
        corrected: want + item.label.punct,
        reason,
        searchFrom: item.line.start,
        searchTo: item.line.textEnd
      });
    }
  });
}

/**
 * Checks the paper can verify without the model:
 * numbered sub-parts whose marks do not add up to the section total,
 * and a section instruction that ends with a question mark (دیں؟ / لکھیں؟).
 * @returns {Array<{ original: string, corrected: string, reason: string, searchFrom: number, searchTo: number }>}
 */
export function auditPaperStructure(sourceText) {
  const source = String(sourceText || '');
  const lines = lineSpans(source);
  const suggestions = [];
  let i = 0;
  while (i < lines.length) {
    if (!isSectionHeader(lines[i].text)) {
      i += 1;
      continue;
    }
    const header = lines[i];
    let j = i + 1;
    while (j < lines.length && !isSectionHeader(lines[j].text)) j += 1;

    const items = [];
    let inAnswer = false;
    for (let k = i + 1; k < j; k += 1) {
      const text = lines[k].text;
      if (!text.trim() || isQuestionEcho(text)) continue;
      if (isAnswerLine(text)) {
        inAnswer = true;
        continue;
      }
      const label = readLabel(text);
      const kind = label ? labelKind(label.key) : null;
      if (inAnswer) {
        if (kind === 'letter') continue;
        if (kind === 'roman' || kind === 'urdu' || markMatches(text).length) inAnswer = false;
        else continue;
      }
      const marks = markMatches(text);
      if (!label && !marks.length) continue;
      items.push({ line: lines[k], label, marks });
    }

    const formula = headingFormula(header.text);
    const headerMarks = markMatches(header.text);
    if (formula && headerMarks.length && Math.abs(formula.product - formula.stated) > 0.001) {
      const total = headerMarks[headerMarks.length - 1];
      suggestions.push({
        original: total.text,
        corrected: total.text.replace(total.digits, toSameDigits(total.digits, formula.product)),
        reason: `${formula.count} × ${formula.each} = ${formula.product}, not ${formula.stated}.`,
        searchFrom: header.start,
        searchTo: header.textEnd
      });
    } else if (!formula && headerMarks.length) {
      const partMarks = items.filter((item) => item.marks.length && !isQuestionEcho(item.line.text) && !isAnswerLine(item.line.text))
        .map((item) => item.marks[item.marks.length - 1].value);
      if (partMarks.length >= 2) {
        const total = headerMarks[headerMarks.length - 1];
        const sum = sumMarks(partMarks);
        if (sum !== total.value) {
          const addition = partMarks.join('+');
          suggestions.push({
            original: total.text,
            corrected: total.text.replace(total.digits, toSameDigits(total.digits, sum)),
            reason: `This section has ${partMarks.length} questions marked ${addition} = ${sum}, but the heading says ${total.value}.`,
            searchFrom: header.start,
            searchTo: header.textEnd
          });
        }
      }
    }

    const letterItems = items.filter((item) => item.label && labelKind(item.label.key) === 'letter');
    const hasParentNumbers = items.some((item) => item.label && (labelKind(item.label.key) === 'roman' || labelKind(item.label.key) === 'urdu'));
    if (hasParentNumbers && letterItems.length) {
      const parents = items.filter((item) => !(item.label && labelKind(item.label.key) === 'letter'));
      const parentStyle = numberingStyle(parents);
      if (parentStyle) auditLabelSequence(parents, parentStyle, suggestions);
      let group = [];
      const flushLetters = () => {
        if (group.length >= 2) auditLabelSequence(group, 'letter', suggestions);
        group = [];
      };
      items.forEach((item) => {
        if (item.label && labelKind(item.label.key) === 'letter') group.push(item);
        else flushLetters();
      });
      flushLetters();
    } else {
      const style = numberingStyle(items);
      if (style) auditLabelSequence(items, style, suggestions);
    }

    const qRe = new RegExp(IMPERATIVE_Q_RE.source, 'g');
    let q;
    while ((q = qRe.exec(header.text))) {
      suggestions.push({
        original: q[0],
        corrected: q[0].replace('؟', '۔'),
        reason: 'This is an instruction, not a question, so the question mark is not needed.',
        searchFrom: header.start,
        searchTo: header.textEnd
      });
    }

    i = j;
  }
  return suggestions;
}

function sectionMarkFacts(source) {
  const lines = lineSpans(source);
  const facts = [];
  let i = 0;
  while (i < lines.length) {
    if (!isSectionHeader(lines[i].text)) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < lines.length && !isSectionHeader(lines[j].text)) j += 1;
    const parts = [];
    for (let k = i + 1; k < j; k += 1) {
      questionMarkValues(lines[k].text).forEach((value) => parts.push(value));
    }
    const headerMarks = markMatches(lines[i].text);
    const formula = headingFormula(lines[i].text);
    const sum = formula ? formula.product : (parts.length >= 2 ? sumMarks(parts) : null);
    if (headerMarks.length && sum != null) {
      const total = headerMarks[headerMarks.length - 1];
      facts.push({
        value: total.value,
        start: lines[i].start + total.index,
        end: lines[i].start + total.index + total.text.length,
        lineStart: lines[i].start,
        lineEnd: lines[i].textEnd,
        sum,
        formulaCorrect: !!(formula && Math.abs(formula.product - formula.stated) < 0.001)
      });
    }
    i = j;
  }
  return facts;
}

function restoreHeadingNumber(original, corrected, fact) {
  const origMarks = markMatches(original);
  const corrMarks = markMatches(corrected);
  if (!origMarks.length || !corrMarks.length) return corrected;
  let out = corrected;
  let from = 0;
  const count = Math.min(origMarks.length, corrMarks.length);
  for (let n = 0; n < count; n += 1) {
    if (Math.abs(origMarks[n].value - fact.value) > 0.001) continue;
    if (Math.abs(corrMarks[n].value - fact.sum) < 0.001) {
      const kept = out.indexOf(corrMarks[n].text, from);
      if (kept >= 0) from = kept + corrMarks[n].text.length;
      continue;
    }
    const idx = out.indexOf(corrMarks[n].text, from);
    if (idx < 0) continue;
    const digits = formatMarkNumber(corrMarks[n].digits, fact.sum);
    const replacement = corrMarks[n].text.replace(corrMarks[n].digits, digits);
    out = out.slice(0, idx) + replacement + out.slice(idx + corrMarks[n].text.length);
    from = idx + replacement.length;
  }
  return out;
}

/**
 * A heading total is the sum of the question marks under it, decimals included.
 * 3.5 + 3.5 = 7, so a heading of 7 stays 7. A model that rewrites it to 14 is put back.
 */
function alignHeadingTotals(sourceText, changes) {
  const facts = sectionMarkFacts(sourceText);
  if (!facts.length) return changes || [];
  return (changes || []).flatMap((change) => {
    let corrected = String(change.corrected ?? '');
    const original = String(change.original ?? '');
    let drop = false;
    facts.forEach((fact) => {
      if (change.start >= fact.end || change.end <= fact.start) return;
      const onHeaderLine = change.start >= fact.lineStart && change.end <= fact.lineEnd + 1;
      if (fact.formulaCorrect && onHeaderLine) {
        drop = true;
        return;
      }
      corrected = restoreHeadingNumber(original, corrected, fact);
    });
    if (drop || corrected === original) return [];
    return [{ ...change, corrected }];
  });
}

function foldIntoCovering(source, changes, item) {
  const needle = item.original;
  const from = item.searchFrom || 0;
  const to = item.searchTo == null ? source.length : item.searchTo;
  const idx = source.indexOf(needle, from);
  if (idx < 0 || idx + needle.length > to) return false;
  const end = idx + needle.length;
  const covering = changes.find((c) => c.start <= idx && c.end >= end);
  if (!covering) return false;
  if (String(covering.corrected || '').includes(item.corrected)) return true;
  if (!String(covering.corrected || '').includes(needle)) return false;
  covering.corrected = covering.corrected.replace(needle, item.corrected);
  const reason = item.reason || '';
  if (reason && !String(covering.reason || '').includes(reason)) {
    covering.reason = [covering.reason, reason].filter(Boolean).join(' ');
  }
  return true;
}

const PLACE_INDEX = {
  ones: 0,
  units: 0,
  tens: 1,
  hundreds: 2,
  thousands: 3,
  'ten thousands': 4,
  'hundred thousands': 5,
  millions: 6,
  'ten millions': 7,
  'hundred millions': 8
};
const PLACE_PHRASE = 'hundred thousands|ten thousands|ten millions|hundred millions|hundreds|thousands|millions|ones|units|tens';
const PLACE_SENTENCE_RE = new RegExp(
  `In\\s+([0-9][0-9,]*)\\s*,\\s*the underlined digit is\\s+([0-9])\\s*\\.\\s*Its place value is\\s+([0-9][0-9,]*)\\s+(${PLACE_PHRASE})(?:\\s*\\(([0-9][0-9,]*)\\))?`,
  'gi'
);

function titlePlace(place) {
  return place.replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
}

function formatPlaceValue(n, sample) {
  const text = String(n);
  if (n >= 1000 || /,/.test(sample || '')) return text.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return text;
}

function placeClaims(source) {
  const claims = [];
  const re = new RegExp(PLACE_SENTENCE_RE.source, 'gi');
  let match;
  while ((match = re.exec(source))) {
    const numberText = match[1];
    const digits = numberText.replace(/,/g, '');
    const digit = match[2];
    const place = String(match[4] || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const placeIndex = PLACE_INDEX[place];
    if (placeIndex == null || placeIndex >= digits.length) continue;
    const atPlace = digits[digits.length - 1 - placeIndex];
    const expectedValue = Number(digit) * (10 ** placeIndex);
    const statedValue = match[5] == null ? expectedValue : Number(String(match[5]).replace(/,/g, ''));
    const placeAt = match[0].toLowerCase().lastIndexOf(String(match[4]).toLowerCase());
    const faceAt = match[0].lastIndexOf(match[3], Math.max(0, placeAt));
    const tail = faceAt >= 0 ? match[0].slice(faceAt) : '';
    claims.push({
      start: match.index,
      end: match.index + match[0].length,
      numberText,
      digit,
      place,
      tail,
      tailStart: match.index + match[0].length - tail.length,
      valid: atPlace === digit && statedValue === expectedValue && match[3] === digit
    });
  }
  return claims;
}

function expectedPlaceClaim(numberText, digit) {
  const digits = String(numberText).replace(/,/g, '');
  const spots = [];
  for (let i = 0; i < digits.length; i += 1) {
    if (digits[i] === digit) spots.push(digits.length - 1 - i);
  }
  if (spots.length !== 1) return null;
  const placeIndex = spots[0];
  const place = Object.keys(PLACE_INDEX).find((name) => PLACE_INDEX[name] === placeIndex && name !== 'units');
  if (!place) return null;
  return { place, value: Number(digit) * (10 ** placeIndex) };
}

function auditPlaceValues(source) {
  const suggestions = [];
  placeClaims(source).forEach((claim) => {
    if (claim.valid) return;
    const expected = expectedPlaceClaim(claim.numberText, claim.digit);
    if (!expected) return;
    const valueText = formatPlaceValue(expected.value, claim.numberText);
    suggestions.push({
      original: claim.tail,
      corrected: `${claim.digit} ${titlePlace(expected.place)} (${valueText})`,
      reason: `In ${claim.numberText}, the digit ${claim.digit} is in the ${expected.place} place. Its place value is ${valueText}.`,
      searchFrom: claim.start,
      searchTo: claim.end
    });
  });
  return suggestions;
}

function paperTotalClaim(source) {
  const lines = lineSpans(source);
  let total = null;
  const sectionMarks = [];
  const questionMarks = [];
  lines.forEach((line) => {
    const stripped = String(line.text || '').replace(/^[\s*#>/_'"“”«»-]+/, '');
    const totalMatch = stripped.match(/total\s*marks\s*:?\s*([0-9]+(?:\.[0-9]+)?)/i);
    if (!total && totalMatch && !/^section\b/i.test(stripped) && !isSectionHeader(line.text)) {
      const value = parseLocalizedNumber(totalMatch[1]);
      const at = line.text.indexOf(totalMatch[1]);
      if (value != null && at >= 0) {
        total = {
          value,
          start: line.start + at,
          end: line.start + at + totalMatch[1].length,
          lineStart: line.start,
          lineEnd: line.textEnd
        };
      }
    }
    if (/^section\b/i.test(stripped)) {
      const marks = markMatches(line.text);
      if (marks.length) sectionMarks.push(marks[marks.length - 1].value);
      return;
    }
    if (isSectionHeader(line.text)) {
      const formula = headingFormula(line.text);
      if (formula) questionMarks.push(formula.product);
      else {
        const marks = markMatches(line.text);
        if (marks.length) questionMarks.push(marks[marks.length - 1].value);
      }
    }
  });
  const parts = sectionMarks.length >= 2 ? sectionMarks : questionMarks;
  if (!total || parts.length < 2) return null;
  return { ...total, sum: sumMarks(parts) };
}

function showMark(n) {
  const rounded = Math.round(Number(n) * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

/** One block per SECTION line: each question's marks, and the banner total. */
function markSections(sourceText) {
  const lines = lineSpans(String(sourceText || ''));
  const bannerIdx = [];
  lines.forEach((line, index) => {
    if (isSectionBanner(line.text)) bannerIdx.push(index);
  });
  return bannerIdx.map((index, n) => {
    const header = lines[index];
    const end = n + 1 < bannerIdx.length ? bannerIdx[n + 1] : lines.length;
    const formulas = [];
    const rows = [];
    for (let k = index + 1; k < end; k += 1) {
      const text = lines[k].text;
      if (!text.trim() || isSectionBanner(text)) continue;
      if (isSectionHeader(text)) {
        const formula = headingFormula(text);
        if (formula) formulas.push(formula);
        continue;
      }
      const values = questionMarkValues(text);
      if (!values.length) continue;
      rows.push({ label: questionLabel(text), marks: values[values.length - 1] });
    }
    const headerMarks = markMatches(header.text);
    const printedMark = headerMarks.length ? headerMarks[headerMarks.length - 1] : null;
    const formula = formulas.length === 1 ? formulas[0] : null;
    const formulaOk = !!(formula && Math.abs(formula.product - formula.stated) < 0.001);
    let counted = null;
    if (formulaOk) counted = formula.product;
    else if (formulas.length >= 2) {
      counted = sumMarks(formulas.map((item) => (
        Math.abs(item.product - item.stated) < 0.001 ? item.product : item.stated
      )));
    } else if (rows.length) counted = sumMarks(rows.map((row) => row.marks));
    const stripped = header.text.replace(/^[\s*#>/_'"“”«»-]+/, '');
    const titleMatch = stripped.match(/^(section\s+[A-Za-z0-9]+)/i);
    return {
      title: titleMatch
        ? titleMatch[1].replace(/\w+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        : 'Section',
      printed: printedMark ? printedMark.value : null,
      counted,
      differs: printedMark != null && counted != null && Math.abs(printedMark.value - counted) > 0.001,
      rows,
      formula: formulaOk ? `${formula.count} × ${formula.each} = ${showMark(formula.product)}` : '',
      lineStart: header.start,
      lineEnd: header.textEnd
    };
  });
}

function rewritesSectionTotal(change, section) {
  if (!section || section.printed == null) return false;
  if (!(change.start < section.lineEnd && change.end > section.lineStart)) return false;
  const origMarks = markMatches(change.original);
  const corrMarks = markMatches(change.corrected);
  return origMarks.some((mark, index) => (
    Math.abs(mark.value - section.printed) < 0.001
    && corrMarks[index]
    && Math.abs(corrMarks[index].value - mark.value) > 0.001
  ));
}

/**
 * Question marks under a section, shown when a total is in dispute.
 * A banner of 56 with 14 × 4 = 56 is not a difference.
 */
export function markTables(sourceText, changes) {
  return markSections(sourceText).filter((section) => (
    section.printed != null
    && section.counted != null
    && (section.differs || (changes || []).some((change) => rewritesSectionTotal(change, section)))
  ));
}

/**
 * 14 × 4 = 56. A suggestion that rewrites a matching section total to 58 is dropped.
 * A real mismatch is rewritten to the counted total.
 */
export function keepSoundSectionBanners(sourceText, changes) {
  const sections = markSections(sourceText);
  if (!sections.length) return changes || [];
  return (changes || []).flatMap((change) => {
    const section = sections.find((item) => rewritesSectionTotal(change, item));
    if (!section) return [change];
    if (!section.differs) return [];
    const corrected = restoreHeadingNumber(change.original, change.corrected, {
      value: section.printed,
      sum: section.counted
    });
    if (corrected === change.original) return [];
    return [{
      ...change,
      corrected,
      reason: `The questions add up to ${showMark(section.counted)}, not ${showMark(section.printed)}.`
    }];
  });
}

/**
 * 56 + 14 = 70. When the printed paper total already equals the section totals,
 * a suggestion that rewrites 70 is dropped.
 */
export function keepSoundPaperTotals(sourceText, changes) {
  const source = String(sourceText || '');
  const claim = paperTotalClaim(source);
  return (changes || []).filter((change) => {
    const reason = String(change.reason || '');
    const corrected = String(change.corrected ?? '');
    const coversTotal = claim && change.start < claim.lineEnd && change.end > claim.lineStart;
    const admitsCorrect = /total is correct/i.test(reason);
    if (coversTotal && admitsCorrect) return false;
    if (!claim || Math.abs(claim.value - claim.sum) > 0.001 || !coversTotal) return true;
    const stated = corrected.match(/total\s*marks\s*:?\s*([0-9]+(?:\.[0-9]+)?)/i);
    const bare = corrected.match(/^\s*([0-9]+(?:\.[0-9]+)?)\s*$/);
    const next = stated ? parseLocalizedNumber(stated[1]) : (bare ? parseLocalizedNumber(bare[1]) : null);
    if (next == null) return true;
    return Math.abs(next - claim.sum) < 0.001;
  });
}

/**
 * 79,870 ends in 0, so that 0 is in the ones place. A suggestion that calls
 * it tens is dropped. A sentence that names the wrong place is rewritten.
 */
function unwrapNumberDollars(text) {
  return String(text || '').replace(/\$([0-9][0-9,]*)\$/g, '$1');
}

function pushPiece(out, text, under) {
  if (!text) return;
  const prev = out[out.length - 1];
  if (prev && prev.under === under) prev.text += text;
  else out.push({ text, under });
}

function noteUnderlinedDigit(map, numberText, digit, place) {
  const digits = String(numberText).replace(/,/g, '');
  const named = String(place || '').toLowerCase().replace(/\s+/g, ' ').trim();
  let index = -1;
  if (named && PLACE_INDEX[named] != null) {
    const at = digits.length - 1 - PLACE_INDEX[named];
    if (digits[at] === digit) index = at;
  }
  if (index < 0) {
    const hits = [];
    for (let i = 0; i < digits.length; i += 1) {
      if (digits[i] === digit) hits.push(i);
    }
    if (hits.length === 1) index = hits[0];
  }
  if (index >= 0) map.set(digits, index);
}

/** Which digit inside 34,567 or 79,870 the answer says is underlined. */
export function paperUnderlineMap(paperText) {
  const text = unwrapNumberDollars(paperText);
  const map = new Map();
  const patterns = [
    new RegExp(
      `In\\s+([0-9][0-9,]*)\\s*,\\s*the underlined digit is\\s+([0-9])(?:\\s*\\.\\s*Its place value is\\s+[0-9][0-9,]*\\s+(${PLACE_PHRASE}))?`,
      'gi'
    ),
    new RegExp(
      `In\\s+([0-9][0-9,]*)\\s*,\\s*place value of\\s+([0-9])\\s*=\\s*[0-9][0-9,]*\\s*\\(\\s*[0-9]\\s+(${PLACE_PHRASE})\\s*\\)`,
      'gi'
    )
  ];
  patterns.forEach((re) => {
    let match;
    while ((match = re.exec(text))) noteUnderlinedDigit(map, match[1], match[2], match[3]);
  });
  return map;
}

/** Split text so the underlined digit can be drawn with a line under it. */
export function splitUnderlinedDigits(text, map) {
  const source = unwrapNumberDollars(text);
  if (source.includes('\u0332')) {
    const out = [];
    let plain = '';
    for (let i = 0; i < source.length; i += 1) {
      if (source[i + 1] === '\u0332') {
        pushPiece(out, plain, false);
        plain = '';
        pushPiece(out, source[i], true);
        i += 1;
        continue;
      }
      if (source[i] === '\u0332') continue;
      plain += source[i];
    }
    pushPiece(out, plain, false);
    return out.length ? out : [{ text: source.replace(/\u0332/g, ''), under: false }];
  }
  if (!map || map.size === 0) return [{ text: source, under: false }];
  const re = /[0-9]{1,3}(?:,[0-9]{3})+|\b[0-9]{4,}\b/g;
  const out = [];
  let last = 0;
  let match;
  while ((match = re.exec(source))) {
    const digits = match[0].replace(/,/g, '');
    if (!map.has(digits)) continue;
    pushPiece(out, source.slice(last, match.index), false);
    const digitIndex = map.get(digits);
    let di = 0;
    let plain = '';
    for (const ch of match[0]) {
      if (ch === ',') {
        plain += ch;
        continue;
      }
      if (di === digitIndex) {
        pushPiece(out, plain, false);
        plain = '';
        pushPiece(out, ch, true);
      } else {
        plain += ch;
      }
      di += 1;
    }
    pushPiece(out, plain, false);
    last = match.index + match[0].length;
  }
  pushPiece(out, source.slice(last), false);
  return out.length ? out : [{ text: source, under: false }];
}

export function keepSoundPlaceValues(sourceText, changes) {
  const source = String(sourceText || '');
  const claims = placeClaims(source);
  if (!claims.length) return changes || [];
  return (changes || []).filter((change) => {
    const hit = claims.find((claim) => change.start < claim.end && change.end > claim.start);
    if (!hit || !hit.valid) return true;
    const next = source.slice(0, change.start) + String(change.corrected ?? '') + source.slice(change.end);
    return placeClaims(next).some((claim) => (
      claim.valid && claim.numberText === hit.numberText && claim.digit === hit.digit && claim.place === hit.place
    ));
  });
}

/**
 * Add structure checks the model missed. If a model edit already covers
 * the same words but left the mistake in its green line, the fix is
 * written into that green line instead of being dropped.
 */
export function mergePaperAudits(sourceText, parsed) {
  const source = String(sourceText || '');
  const base = parsed || { verdict: '', changes: [], dropped: 0, unmatched: [], error: '' };
  const changes = [...(base.changes || [])];
  const used = changes
    .filter((c) => typeof c.start === 'number' && typeof c.end === 'number')
    .map((c) => ({ start: c.start, end: c.end }));
  auditPaperStructure(source).forEach((item, index) => {
    const start = findInRange(source, item.original, item.searchFrom, item.searchTo, used);
    if (start < 0) {
      foldIntoCovering(source, changes, item);
      return;
    }
    const end = start + item.original.length;
    used.push({ start, end });
    let id = `audit-${index + 1}`;
    while (changes.some((c) => c.id === id)) id = `${id}-x`;
    changes.push({
      id,
      original: item.original,
      corrected: item.corrected,
      reason: item.reason,
      start,
      end
    });
  });
  auditPlaceValues(source).forEach((item, index) => {
    const start = findInRange(source, item.original, item.searchFrom, item.searchTo, used);
    if (start < 0) {
      foldIntoCovering(source, changes, item);
      return;
    }
    const end = start + item.original.length;
    used.push({ start, end });
    let id = `place-${index + 1}`;
    while (changes.some((c) => c.id === id)) id = `${id}-x`;
    changes.push({
      id,
      original: item.original,
      corrected: item.corrected,
      reason: item.reason,
      start,
      end
    });
  });
  const aligned = keepSoundSectionBanners(
    source,
    keepSoundPaperTotals(source, keepSoundPlaceValues(source, alignHeadingTotals(source, changes)))
  );
  aligned.sort((a, b) => a.start - b.start);
  return {
    verdict: aligned.length ? 'ISSUES_FOUND' : (base.verdict || ''),
    changes: aligned,
    dropped: base.dropped || 0,
    unmatched: base.unmatched || [],
    error: base.error || ''
  };
}
